import React, { memo, useCallback, useMemo } from 'react'
import { Box, ToggleButton, Typography } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { selectMultiStrikeChartStyles } from '../../../../../selectors/multiStrikeChart'
import { setMultiStrikeChartStyles, MultiStrikeChartStylesTypes } from '../../../../../reducer/multiStrikeChart.reducer'
import { LineType } from 'lightweight-charts'
import { ChartViewKey } from '../../../../../assets/constants'
import { updateMultiStrikeChartConfig } from '../../../../../reducer/chart.reducer'
import { selectMultiStrikeChartConfigStyles } from '../../../../../selectors/chart'
import AnalyticsManager from '../../../../../analytics/AnalyticsManager'
import SeriesOutputPlotTypesView from './OutputPlotTypes'
import MultiStrikeStrikeLines from './MultiStrikeStrikeLines'
import { MultiStrikeColorPaletteId } from '../../../multiStrikeColorPalettes'
import MultiStrikeUnderlyingStyle from './MultiStrikeUnderlyingStyle'
import { ChartLineStyle, LineThickness } from '../../../../colorSelector/lineStyle.types'
import {
    chartSettingsGridRowSx,
    chartSettingsLabelSx,
    chartSettingsRowControlsSx,
    chartSettingsSectionDividerSx,
    chartSettingsSectionTitleSx,
} from '../../../../chartSettings/chartSettingsLayout'

export const thicknessButtons = () => {
    return (
        [<ToggleButton key={1} value={1} aria-label="left aligned">
            <Box sx={{ height: '1px', backgroundColor: (theme) => theme.palette.text.primary, width: 10 }} />
        </ToggleButton>,
        <ToggleButton key={2} value={2} aria-label="centered">
            <Box sx={{ height: '2px', backgroundColor: (theme) => theme.palette.text.primary, width: 10 }} />
        </ToggleButton>,
        <ToggleButton key={3} value={3} aria-label="centered">
            <Box sx={{ height: '3px', backgroundColor: (theme) => theme.palette.text.primary, width: 10 }} />
        </ToggleButton>,
        <ToggleButton key={4} value={4} aria-label="right aligned">
            <Box sx={{ height: '4px', backgroundColor: (theme) => theme.palette.text.primary, width: 15 }} />
        </ToggleButton>]
    )
}

export const GroupOutputs = ({ chartId }: { chartId: string }) => {
    const dispatch = useDispatch()
    const isWidget = useMemo(() => ChartViewKey.MULTI_STRIKES_CHART !== chartId, [chartId])
    const styles = useSelector(isWidget ? selectMultiStrikeChartConfigStyles(chartId) : selectMultiStrikeChartStyles)
    const {
        lineThickness,
        lineType,
        colorPaletteId,
        customCallBaseColor,
        customPutBaseColor,
        customCallHueShift,
        customPutHueShift,
    } = styles

    const updateStyles = useCallback((partialStyles: Partial<MultiStrikeChartStylesTypes>) => {
        const nextStyles = {
            ...styles,
            ...partialStyles,
        }
        if (isWidget) {
            dispatch(updateMultiStrikeChartConfig({
                viewKey: chartId,
                config: {
                    styles: nextStyles,
                },
            }))
        } else {
            dispatch(setMultiStrikeChartStyles(nextStyles))
        }
    }, [chartId, dispatch, isWidget, styles])

    const handlePlotTypeChange = useCallback((plotType: LineType) => {
        updateStyles({ lineType: plotType })
    }, [updateStyles])

    const handleColorPaletteChange = useCallback((paletteId: MultiStrikeColorPaletteId) => {
        AnalyticsManager.logEvent('Setting Style Changed', { source: 'multistrike', type: 'colorPalette' })
        updateStyles({ colorPaletteId: paletteId })
    }, [updateStyles])

    const handleCustomCallBaseColorChange = useCallback((color: string) => {
        updateStyles({ colorPaletteId: 'custom', customCallBaseColor: color })
    }, [updateStyles])

    const handleCustomPutBaseColorChange = useCallback((color: string) => {
        updateStyles({ colorPaletteId: 'custom', customPutBaseColor: color })
    }, [updateStyles])

    const handleCustomCallHueShiftChange = useCallback((hueShift: number) => {
        updateStyles({ colorPaletteId: 'custom', customCallHueShift: hueShift })
    }, [updateStyles])

    const handleCustomPutHueShiftChange = useCallback((hueShift: number) => {
        updateStyles({ colorPaletteId: 'custom', customPutHueShift: hueShift })
    }, [updateStyles])

    const handleStrikeThicknessChange = useCallback((thickness: LineThickness) => {
        AnalyticsManager.logEvent('Setting Style Changed', { source: 'multistrike', type: 'thickness' })
        updateStyles({ lineThickness: thickness })
    }, [updateStyles])

    const handleStrikeLineStyleChange = useCallback((style: ChartLineStyle) => {
        updateStyles({ strikeLineStyle: style })
    }, [updateStyles])

    return (
        <Box>
            <Typography variant="subtitle1" sx={chartSettingsSectionTitleSx}>
                UNDERLYING
            </Typography>
            <Box sx={chartSettingsGridRowSx}>
                <Typography sx={chartSettingsLabelSx}>Line</Typography>
                <Box sx={chartSettingsRowControlsSx}>
                    <MultiStrikeUnderlyingStyle
                        underlyingColor={styles.underlyingColor}
                        underlyingThickness={styles.underlyingThickness ?? lineThickness}
                        underlyingLineStyle={styles.underlyingLineStyle}
                        underlyingLineType={styles.underlyingLineType}
                        onChange={updateStyles}
                    />
                </Box>
            </Box>
            <Typography
                variant="subtitle1"
                sx={{ ...chartSettingsSectionTitleSx, ...chartSettingsSectionDividerSx }}
            >
                STRIKE LINES
            </Typography>
            <Box sx={chartSettingsGridRowSx}>
                <Typography sx={chartSettingsLabelSx}>Lines</Typography>
                <Box sx={{ ...chartSettingsRowControlsSx, flexWrap: 'wrap' }}>
                    <MultiStrikeStrikeLines
                        colorPaletteId={colorPaletteId}
                        customCallBaseColor={customCallBaseColor}
                        customPutBaseColor={customPutBaseColor}
                        customCallHueShift={customCallHueShift}
                        customPutHueShift={customPutHueShift}
                        thickness={lineThickness}
                        lineStyle={styles.strikeLineStyle}
                        onPaletteChange={handleColorPaletteChange}
                        onCustomCallBaseColorChange={handleCustomCallBaseColorChange}
                        onCustomPutBaseColorChange={handleCustomPutBaseColorChange}
                        onCustomCallHueShiftChange={handleCustomCallHueShiftChange}
                        onCustomPutHueShiftChange={handleCustomPutHueShiftChange}
                        onThicknessChange={handleStrikeThicknessChange}
                        onLineStyleChange={handleStrikeLineStyleChange}
                    />
                    <SeriesOutputPlotTypesView lineType={lineType} onSelected={handlePlotTypeChange} />
                </Box>
            </Box>
        </Box>
    )
}

export default memo(GroupOutputs)
